import {
  Alert,
  Button,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../Language";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useGlobalConfig } from "../../GlobalConfig";

import { Patron } from "../../AccountSummaryAlerts";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import axios from "axios";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { AccountHolder, useAccount } from "../../Account";
import { OBRSettingsProvider } from "../../OBRSettings";
import { NotificationPreferences } from "./NotificationPreferences";
import { PhoneField } from "../obr/Phone";
import { FormTextField } from "../../FormTextField";
import { PrimaryEmail } from "../obr/PrimaryEmail";
import { AdditionalEmailGroup } from "../obr/AdditionalEmailGroup";
import { AddressFieldGroup } from "../obr/AddressFieldGroup";
import { PrimaryPhone } from "../obr/PrimaryPhone";
import { MobilePhone } from "../obr/MobilePhone";

interface SaveUserContactInfoReturn {
  isValidPin: boolean;
  success: boolean;
  isPatronInfoSaved: boolean;
  isEmailInfoSaved: boolean;
}

export interface ContactInfoFormInput
  extends Pick<
    Patron,
    | "address1"
    | "address2"
    | "city"
    | "state"
    | "zipcode"
    | "phone"
    | "altPhone"
    | "smsPhone"
    | "smsGatewayId"
    | "email"
    | "email2"
    | "email3"
    | "pin"
    | "overdueNotificationTypes"
    | "arrivedHoldNotificationTypes"
    | "expiredCardNotificationTypes"
    | "finalOverdueNotificationTypes"
    | "cancelledHoldNotificationTypes"
    | "friendlyWarningNotificationTypes"
    | "automaticRenewalNotificationTypes"
  > {}

export const EditContactInfoPage: React.FC = () => {
  const { patron } = useAccount();

  const formMethods = useForm<ContactInfoFormInput>({
    defaultValues: { ...patron, pin: "" },
    mode: "onTouched",
  });

  const { handleSubmit, watch } = formMethods;

  const [error, setError] = React.useState<string>();

  const onSubmit: SubmitHandler<ContactInfoFormInput> = async (data) => {
    const {
      pin,
      address1,
      address2,
      city,
      state,
      zipcode,
      phone,
      altPhone,
      smsGatewayId,
      smsPhone,
      email,
      email2,
      email3,
      overdueNotificationTypes,
      arrivedHoldNotificationTypes,
      expiredCardNotificationTypes,
      finalOverdueNotificationTypes,
      cancelledHoldNotificationTypes,
      friendlyWarningNotificationTypes,
      automaticRenewalNotificationTypes,
    } = data;
    const updatedPatron = {
      ...patron,
      address1,
      address2,
      city,
      state,
      zipcode,
      phone,
      altPhone,
      smsGatewayId,
      smsPhone,
      email,
      email2,
      email3,
      overdueNotificationTypes,
      arrivedHoldNotificationTypes,
      expiredCardNotificationTypes,
      finalOverdueNotificationTypes,
      cancelledHoldNotificationTypes,
      friendlyWarningNotificationTypes,
      automaticRenewalNotificationTypes,
    };

    setError(null);
    const { data: responseData } = await axios.post<SaveUserContactInfoReturn>(
      `/account/update/contactInformation`,
      {
        patron: updatedPatron,
        pin,
      }
    );

    if (responseData.success) {
      SuccessMessageControl.show(
        translate("successUpdatingContactInformationMessage")
      );
      await AccountHolder.fetchPatron(true); // Trigger an update to the patron data in context
      navigate("/myaccount/summary");
    } else if (responseData.isValidPin === false) {
      setError(translate("invalidPinErrorMessage"));
    } else {
      setError(translate("errorUpdatingContactInformationMessage"));
    }
  };

  const notificationTypes = [
    watch("arrivedHoldNotificationTypes"),
    watch("automaticRenewalNotificationTypes"),
    watch("cancelledHoldNotificationTypes"),
    watch("overdueNotificationTypes"),
    watch("expiredCardNotificationTypes"),
    watch("finalOverdueNotificationTypes"),
    watch("friendlyWarningNotificationTypes"),
  ];

  const { translate } = useLanguage();
  const { publicHostInformation, smsGateways, ldapEnabled, adfsSettings } =
    useGlobalConfig();
  const navigate = useNavigate();

  const usePIN =
    publicHostInformation?.usePin && !ldapEnabled && !adfsSettings?.enabled;

  return (
    <OBRSettingsProvider>
      <FormProvider {...formMethods}>
        <Toolbar disableGutters={true}>
          <IconButton
            aria-label="back"
            onClick={() => navigate("/myaccount/summary")}
          >
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="h5">
            {translate("editYourContactInformation")}
          </Typography>
        </Toolbar>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack gap={2} direction="column">
            {error && <Alert severity="error">{error}</Alert>}
            <AddressFieldGroup<ContactInfoFormInput>
              line1={"address1"}
              line2="address2"
              city={"city"}
              state={"state"}
              zip={"zipcode"}
              disabled={!publicHostInformation?.addressEditable}
              notificationTypes={notificationTypes}
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              <PrimaryPhone<ContactInfoFormInput>
                name={"phone"}
                disabled={!publicHostInformation?.phoneNumberEditable}
                notificationTypes={notificationTypes}
              />
              <PhoneField<ContactInfoFormInput>
                sx={{ flexGrow: 1 }}
                disabled={!publicHostInformation?.phoneNumberEditable}
                name="altPhone"
                label={translate("ci_altPhoneNumber")}
              />
            </Stack>
            <MobilePhone<ContactInfoFormInput>
              number={"smsPhone"}
              carrier={"smsGatewayId"}
              disabled={!publicHostInformation?.phoneNumberEditable}
              notificationTypes={notificationTypes}
              smsGateways={smsGateways}
            />
            <PrimaryEmail<ContactInfoFormInput>
              name={"email"}
              email2={"email2"}
              email3={"email3"}
              disabled={!publicHostInformation?.emailEditable}
              notificationTypes={notificationTypes}
            />
            <AdditionalEmailGroup<ContactInfoFormInput>
              email2="email2"
              email3="email3"
              disabled={!publicHostInformation?.emailEditable}
            />

            <NotificationPreferences<ContactInfoFormInput>
              expiredCardName={"expiredCardNotificationTypes"}
              friendlyWarningName={"friendlyWarningNotificationTypes"}
              overdueName={"overdueNotificationTypes"}
              finalOverdueName={"finalOverdueNotificationTypes"}
              arrivedHoldName={"arrivedHoldNotificationTypes"}
              cancelledHoldName={"cancelledHoldNotificationTypes"}
              automaticRenewalName={"automaticRenewalNotificationTypes"}
              issuingLocation={patron.issuingBranch}
            ></NotificationPreferences>

            {usePIN && (
              <FormTextField<ContactInfoFormInput>
                name="pin"
                label={translate("libraryPinTab")}
                options={{
                  required: translate("isRequired", {
                    LABEL: translate("libraryPinTab"),
                  }),
                }}
                helperText={translate("enterOldPinForSecurityPurposes")}
                type="password"
              />
            )}

            <Stack direction="row" gap={1}>
              <Button type="submit" variant="contained">
                {translate("save")}
              </Button>
              <Button type="button" component={Link} to="/myaccount/summary">
                {translate("cancel")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </OBRSettingsProvider>
  );
};
